import api from "./api"
import { RegistrarLogErro } from "./clientefiel"

export interface PagamentoResumo {
    descricao: string
    total: number
}

export interface CaixaResumo {
    id: number
    abertoEm: number
    fechadoEm: number | null
    dinheiroInicial: string
    totalFechamentoCaixa: string | null
    quebraCaixa: string | null
    observacao: string | null
    statusFuncionamento: number
    quantidadePedidos: number
    somaVenda: string
    somaVendaDinheiro: string
    somaVendaCartao: string
    somaVendaiFoodOnline: string
    somaVendaPixOnline: string
    somaVendaPagSeguroOnline: string
    somaVendaOutros: string
    somaRetiradasDoCaixa: string
    pagamentos: PagamentoResumo[]
}

export interface ErroApi {
    retornoErro: number
    mensagem: string
}

// ---- Impressao de fechamento (endpoint caixa/ImprimirFechamentoCaixa) ----

export interface FechamentoCabecalho {
    caixaId: number
    estabelecimentoNome: string | null
    aberturaEm: number | null
    fechamentoEm: number | null
    usuario: string | null
    dataImpressao: number
}

export interface FechamentoResumoGeral {
    totalBrutoVendas: number
    descontos: number
    cancelamentos: number
    taxasEntrega: number
    totalLiquido: number
    qtdPedidos: number
    ticketMedio: number
}

export interface FechamentoFormaPagamento {
    descricao: string
    valor: number
}

export interface FechamentoCanal {
    titulo: string
    formas: FechamentoFormaPagamento[]
    subtotal: number
    // Presentes apenas no canal iFood quando ha cupom reembolsado
    cupomIFood?: number
    aReceber?: number
}

export interface FechamentoTotais {
    totalVendido: number
    reembolsoIFood: number
    totalAReceber: number
}

export interface FechamentoItemVendido {
    descricao: string
    quantidade: number
    // Presente só nos produtos (total da linha, já com os adicionais)
    valor?: number
}

export interface FechamentoItensVendidos {
    produtos: FechamentoItemVendido[]
    adicionais: FechamentoItemVendido[]
}

export interface FechamentoImpressao {
    cabecalho: FechamentoCabecalho
    resumoGeral: FechamentoResumoGeral
    canais: FechamentoCanal[]
    // Consolidado por forma de pagamento (soma cada forma em todos os canais)
    porFormaPagamento: FechamentoFormaPagamento[]
    totais: FechamentoTotais
    // Só vem quando a loja ligou "Imprimir itens vendidos" na tela do Caixa
    itensVendidos?: FechamentoItensVendidos
}

// Configurações da impressão do fechamento editáveis na tela do Caixa
export interface ConfiguracaoFechamento {
    imprimirItensVendidos: boolean
}

type RespostaErroRaw = { codErro: number; mensagem?: string }

function isErroBackend(data: unknown): data is RespostaErroRaw {
    return (
        typeof data === "object" &&
        data !== null &&
        !Array.isArray(data) &&
        "codErro" in data &&
        (data as RespostaErroRaw).codErro > 0
    )
}

function erroParaRespostaRaw(error: unknown): RespostaErroRaw {
    const axiosError = error as { message?: string; response?: { data?: unknown } }
    const data = axiosError.response?.data
    if (
        typeof data === "object" &&
        data !== null &&
        "codErro" in data &&
        (data as { codErro: number }).codErro > 0
    ) {
        const d = data as { codErro: number; mensagem?: string }
        return { codErro: d.codErro, mensagem: d.mensagem }
    }
    if (axiosError.message === "Network Error") {
        return { codErro: 1, mensagem: "Verifique sua conexão!" }
    }
    return { codErro: 1, mensagem: axiosError.message || "Erro desconhecido" }
}

// Detalhamento do caixa gerado pelo SERVIDOR (mesmo texto do "Resumo do Movimento",
// via ResumoDiaService) para o dia do caixa. Recebe o timestamp de abertura do caixa.
export const obterDetalhamentoFechamento = async function (
    authorization: string,
    timestamp: number
): Promise<{ detalhamento: string } | ErroApi> {
    try {
        const response = await api
            .get<{ detalhamento: string } | RespostaErroRaw>(
                `caixa/ObterDetalhamentoFechamento?timestamp=${timestamp}`,
                {
                    headers: { "Content-Type": "application/json", Authorization: authorization },
                }
            )
            .catch(function (error) {
                return { data: erroParaRespostaRaw(error) as RespostaErroRaw }
            })

        if (isErroBackend(response.data)) {
            return { retornoErro: response.data.codErro, mensagem: response.data.mensagem ?? "" }
        }
        return response.data as { detalhamento: string }
    } catch (error) {
        RegistrarLogErro(error, "obterDetalhamentoFechamento", String(timestamp))
        return { retornoErro: 1, mensagem: (error as Error).message }
    }
}

export const obterHistoricoCaixa = async function (
    authorization: string,
    dataInicio?: string,
    dataFim?: string,
    idEstabelecimento?: number
): Promise<CaixaResumo[] | ErroApi> {
    try {
        const query = new URLSearchParams()
        if (dataInicio) query.set("dataInicio", dataInicio)
        if (dataFim) query.set("dataFim", dataFim)
        if (idEstabelecimento != null) query.set("idEstabelecimento", String(idEstabelecimento))
        const params = query.toString() ? `?${query.toString()}` : ""
        const response = await api
            .get<CaixaResumo[] | RespostaErroRaw>(`caixa/ObterHistoricoCaixa${params}`, {
                headers: { "Content-Type": "application/json", Authorization: authorization },
            })
            .catch(function (error) {
                return { data: erroParaRespostaRaw(error) as RespostaErroRaw }
            })

        if (isErroBackend(response.data)) {
            return { retornoErro: response.data.codErro, mensagem: response.data.mensagem ?? "" }
        }
        if (response.data === null || response.data === undefined) {
            return []
        }
        return response.data as CaixaResumo[]
    } catch (error) {
        RegistrarLogErro(error, "obterHistoricoCaixa", dataInicio ?? "")
        return { retornoErro: 1, mensagem: (error as Error).message }
    }
}

// Fechamento de um caixa em formato estruturado para a interface montar a impressao.
export const imprimirFechamentoCaixa = async function (
    authorization: string,
    caixaId: number,
    idEstabelecimento?: number
): Promise<FechamentoImpressao | ErroApi> {
    try {
        const estabParam = idEstabelecimento != null ? `&idEstabelecimento=${idEstabelecimento}` : ""
        const response = await api
            .get<FechamentoImpressao | RespostaErroRaw>(
                `caixa/ImprimirFechamentoCaixa?caixaId=${caixaId}${estabParam}`,
                {
                    headers: { "Content-Type": "application/json", Authorization: authorization },
                }
            )
            .catch(function (error) {
                return { data: erroParaRespostaRaw(error) as RespostaErroRaw }
            })

        if (isErroBackend(response.data)) {
            return { retornoErro: response.data.codErro, mensagem: response.data.mensagem ?? "" }
        }
        return response.data as FechamentoImpressao
    } catch (error) {
        RegistrarLogErro(error, "imprimirFechamentoCaixa", String(caixaId))
        return { retornoErro: 1, mensagem: (error as Error).message }
    }
}

export const obterConfiguracaoFechamento = async function (
    authorization: string,
    idEstabelecimento?: number
): Promise<ConfiguracaoFechamento | ErroApi> {
    try {
        const estabParam = idEstabelecimento != null ? `?idEstabelecimento=${idEstabelecimento}` : ""
        const response = await api
            .get<ConfiguracaoFechamento | RespostaErroRaw>(`caixa/ConfiguracaoFechamento${estabParam}`, {
                headers: { "Content-Type": "application/json", Authorization: authorization },
            })
            .catch(function (error) {
                return { data: erroParaRespostaRaw(error) as RespostaErroRaw }
            })

        if (isErroBackend(response.data)) {
            return { retornoErro: response.data.codErro, mensagem: response.data.mensagem ?? "" }
        }
        return response.data as ConfiguracaoFechamento
    } catch (error) {
        RegistrarLogErro(error, "obterConfiguracaoFechamento", String(idEstabelecimento ?? ""))
        return { retornoErro: 1, mensagem: (error as Error).message }
    }
}

export const salvarConfiguracaoFechamento = async function (
    authorization: string,
    configuracao: ConfiguracaoFechamento,
    idEstabelecimento?: number
): Promise<ConfiguracaoFechamento | ErroApi> {
    try {
        const estabParam = idEstabelecimento != null ? `?idEstabelecimento=${idEstabelecimento}` : ""
        const response = await api
            .post<ConfiguracaoFechamento | RespostaErroRaw>(
                `caixa/SalvarConfiguracaoFechamento${estabParam}`,
                configuracao,
                {
                    headers: { "Content-Type": "application/json", Authorization: authorization },
                }
            )
            .catch(function (error) {
                return { data: erroParaRespostaRaw(error) as RespostaErroRaw }
            })

        if (isErroBackend(response.data)) {
            return { retornoErro: response.data.codErro, mensagem: response.data.mensagem ?? "" }
        }
        return response.data as ConfiguracaoFechamento
    } catch (error) {
        RegistrarLogErro(error, "salvarConfiguracaoFechamento", String(idEstabelecimento ?? ""))
        return { retornoErro: 1, mensagem: (error as Error).message }
    }
}
